import React from 'react';
import { Button, Tooltip, Space, message } from 'antd';
import copy from 'copy-to-clipboard';
import { CopyOutlined } from '@ant-design/icons';
import TagValueAutoComplete from './TagValueAutoComplete';
import TagKeyAutoComplete from './TagKeyAutoComplete';
import DeleteSvg from '@src/icons/trash.svg';
import { isEmptyOrNotEmptyOp, isOnlyStars } from '@shared/utils/eventBuilder';
import { EStringOp } from '@shared/types';
import { CusSelect, RuleRow, OrWidth, OrTag, ReadOnly } from './styled';

export const getOperatorOptions = () => {
  const operatorOptions = Object.values(EStringOp).map((value) => {
    let label =
      {
        [EStringOp.EQUALS]: 'equals',
        [EStringOp.NOT_EQ]: 'not equals',
      }[value] || value;
    let tooltipTitle = '';

    // add tooltip
    if (value === 'is not empty') {
      tooltipTitle = `Must have at least 1 character`;
    } else if (value === 'is empty') {
      tooltipTitle = `Matches "" or null values`;
    } else if (value === 'match pattern') {
      tooltipTitle = `Pattern match with * wildcard only. Ex: /profiles/v4/* matches /profiles/v4/123`;
    }

    if (tooltipTitle) {
      label = <Tooltip title={tooltipTitle}>{label}</Tooltip>;
    }
    return {
      value,
      label,
    };
  });
  const operatorOptionsExcludingNull = operatorOptions.filter(
    (option) => option.value !== EStringOp.IS_EMPTY && option.value !== EStringOp.IS_NOT_EMPTY,
  );

  return {
    operatorOptions,
    operatorOptionsExcludingNull,
  };
};

const RuleItem: React.FC<any> = ({
  rawEventName,
  rule,
  disabled,
  onChange,
  showHeader = true,
  onDelete,
  errorFields = [],
}) => {
  const { operatorOptions } = getOperatorOptions();

  const isTagValueEmptyValue = !rule.tag_value.value?.length || rule.tag_value.value[0] === '';
  const isTagKeyEmptyValue = !rule.tag_key.value?.length || rule.tag_key.value[0] === '';
  const isValidEmpty = isTagKeyEmptyValue && isTagValueEmptyValue;

  const isTagValueError =
    errorFields.includes('tag_key_value') && isTagValueEmptyValue && !isValidEmpty;
  const isInvalidTagValue =
    rule.tag_value.op === EStringOp.MATCH_PATTERN &&
    errorFields.includes('match_pattern_error') &&
    isOnlyStars(rule.tag_value.value[0]);

  const tagValueStatus = isTagValueError || isInvalidTagValue ? 'error' : undefined;
  const tagKeyStatus =
    errorFields.includes('tag_key_value') && isTagKeyEmptyValue && !isValidEmpty
      ? 'error'
      : undefined;

  const renderTagValue = () => {
    if (isEmptyOrNotEmptyOp(rule.tag_value.op)) {
      return null;
    }

    if (disabled) {
      return (
        <ReadOnly title={rule.tag_value.join(',')} style={{ width: '185px' }}>
          {rule.tag_value}
        </ReadOnly>
      );
    }

    return (
      <div style={{ flex: 1 }}>
        <TagValueAutoComplete
          disabled={disabled}
          rawEventName={rawEventName}
          rule={rule}
          onChange={(option) => {
            const trimmedValue = option?.value?.trim();
            rule.tag_value.value = trimmedValue ? [trimmedValue] : [];
            rule.tag_value.label = [option?.label];
            onChange(rule);
          }}
          placeholder="Tag Value..."
          filterOption={true}
          status={tagValueStatus}
        />
      </div>
    );
  };

  const displayValue = (rule?.tag_value?.label?.[0] ?? rule?.tag_value?.value?.[0] ?? '') as string;

  return (
    <RuleRow>
      <OrWidth>{showHeader && <OrTag>OR</OrTag>}</OrWidth>
      {/*new workflow remove this by ux requirement.*/}
      {/*<span className={styles.numberTag}>{`${index + 1}.${num + orIndex + 1}`}</span>*/}
      {disabled ? (
        <ReadOnly title={rule.tag_key.value[0]} style={{ width: '123px' }}>
          {rule.tag_key.value[0]}
        </ReadOnly>
      ) : (
        <div style={{ width: '30%', flexShrink: 1, flexGrow: 0, maxWidth: '30%', minWidth: '30%' }}>
          <TagKeyAutoComplete
            disabled={disabled}
            rawEventName={rawEventName}
            rule={rule}
            onChange={(value) => {
              const trimmedValue = value?.trim();
              rule.tag_key.value = trimmedValue ? [trimmedValue] : [];
              rule.tag_value.value = [];
              rule.tag_value.label = rule.tag_value?.label ? [] : undefined;
              onChange(rule);
            }}
            filterOption={false}
            status={tagKeyStatus}
            placeholder="When Tag Key..."
          />
        </div>
      )}
      <CusSelect
        disabled={disabled}
        style={{ width: 'fit-content', minWidth: '83px' }}
        popupMatchSelectWidth={120}
        options={operatorOptions}
        value={rule.tag_value.op}
        onChange={(value) => {
          rule.tag_value.op = value;
          if (value == EStringOp.IS_EMPTY || value === EStringOp.IS_NOT_EMPTY) {
            rule.tag_value.value = [];
          }
          onChange(rule);
        }}
      />
      {renderTagValue()}
      <Space size={0} style={{ marginLeft: 'calc(var(--rule-row-gap) * -1 + 2px)' }}>
        <Button
          disabled={!displayValue}
          type="text"
          icon={<CopyOutlined />}
          style={{ color: '#898989' }}
          onClick={() => {
            copy(displayValue);
            message.info('Copied to clipboard');
          }}
        />
        {!disabled && <Button type="text" icon={<DeleteSvg />} onClick={() => onDelete()} />}
      </Space>
    </RuleRow>
  );
};

export default RuleItem;
